import React, { useState, useEffect, useRef } from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Toast } from "primereact/toast";
import { Link } from 'react-router-dom';
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";
import { Button } from "primereact/button";
import { Card } from "primereact/card";
import { InputText } from "primereact/inputtext";
import { Calendar } from "primereact/calendar";
import { Dropdown } from "primereact/dropdown";
import { useNavigate } from "react-router-dom";
import { makeRequest2 } from "../../../utils/makeRequest";
import { useFacilityStore } from "../../../app/z-store/store";
import { get_property_managed_invoices } from "../../../utils/urls";
import { Toolbar } from "primereact/toolbar";
import Layout from "../component/layout";
import { toastify } from '../../../utils/toast';
import { getItem } from '../../../utils/localStorage';
import PropertyManagerContractDetailsDialog from "./contracts/PropertyManagerContractDetails";

const PropertyManagedInvoices = () => {
  const navigate = useNavigate();
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchKeyword, setSearchKeyword] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [dateFilters, setDateFilters] = useState({
    startDate: null,
    endDate: null,
  });
  const [showContractDetailsDialog, setShowContractDetailsDialog] = useState(false);
  const [selectedContractId, setSelectedContractId] = useState(null);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalInvoices, setTotalInvoices] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const itemsPerPage = 10;

  const toast = useRef(null);
  const selectedFacilityId = useFacilityStore((state) => state.facilityId);

  // Filter options
  const statusOptions = [
    { label: 'All Status', value: '' },
    { label: 'Paid', value: 'Paid' },
    { label: 'Unpaid', value: 'Unpaid' },
    { label: 'Partially Paid', value: 'Partially Paid' },
    { label: 'Overdue', value: 'Overdue' },
    { label: 'Cancelled', value: 'Cancelled' }
  ];

  const fetchInvoices = async (page = 1) => {
    try {
      setLoading(true);

      // Build query parameters
      const queryParams = new URLSearchParams({
        page: page.toString(),
        limit: itemsPerPage.toString()
      });

      if (statusFilter) queryParams.append('status', statusFilter);
      if (dateFilters.startDate) queryParams.append('startDate', dateFilters.startDate.toISOString());
      if (dateFilters.endDate) queryParams.append('endDate', dateFilters.endDate.toISOString());

      const response = await makeRequest2(
        `${get_property_managed_invoices}/${selectedFacilityId}?${queryParams.toString()}`,
        "GET"
      );

      console.log("PMResponse:", response);

      if (response.success) {
        const invoiceData = response.data?.data || [];
        const { totalInvoices, pagination } = response.data || {};

        // Transform the data to ensure it matches our needs
        const transformedData = invoiceData.map(invoice => ({
          ...invoice,
          clientInfo: invoice.clientInfo || {
            fullName: 'No Client Assigned',
            firstName: '',
            lastName: ''
          }
        }));

        setInvoices(transformedData);
        setTotalInvoices(totalInvoices || 0);
        setTotalPages(pagination?.totalPages || 0);
        setCurrentPage(pagination?.page || 1);
      } else {
        toast.current.show({
          severity: "error",
          summary: "Error",
          detail: response.error || "Failed to fetch property management invoices"
        });
        setInvoices([]);
      }
    } catch (error) {
      toast.current.show({
        severity: "error",
        summary: "Error",
        detail: error.message || "Error loading property management invoices"
      });
      setInvoices([]);
    } finally {
      setLoading(false);
    }
  };

  const digitaxTemplate = (rowData) => {
    const digitax = rowData.digitax;

    if (!digitax) {
      return (
        <span className="badge bg-secondary">
          Not Submitted
        </span>
      );
    }

    if (digitax.synced === true) {
      return (
        <span
          className="badge bg-success"
          title={`DigiTax status: ${digitax.status || "Submitted"}`}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: "5px",
            padding: "6px 10px",
            fontSize: "12px",
            fontWeight: 500,
          }}
        >
          <i className="pi pi-check-circle" />
          Submitted
        </span>
      );
    }

    if (digitax.lastError || digitax.errorMessage) {
      return (
        <span
          className="badge bg-danger"
          title={digitax.lastError || digitax.errorMessage}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: "5px",
            padding: "6px 10px",
            fontSize: "12px",
            fontWeight: 500,
          }}
        >
          <i className="pi pi-times-circle" />
          Failed
        </span>
      );
    }

    return (
      <span
        className="badge bg-warning text-dark"
        title={`DigiTax status: ${digitax.status || "Processing"}`}
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: "5px",
          padding: "6px 10px",
          fontSize: "12px",
          fontWeight: 500,
        }}
      >
        <i className="pi pi-clock" />
        Pending
      </span>
    );
  };

  const handleViewInvoice = (invoiceId) => {
    navigate(`/facility/levy_management/invoice/${invoiceId}`, {
      state: { facilityId: selectedFacilityId }
    });
  };

  const handleViewContract = (contractId) => {
    setSelectedContractId(contractId);
    setShowContractDetailsDialog(true);
  };

  const formatDate = (value) => {
    return new Date(value).toLocaleDateString();
  };

  const formatCurrency = (value, currencyCode = 'KES') => {
    try {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: currencyCode,
        minimumFractionDigits: 2
      }).format(value);
    } catch (error) {
      return `${currencyCode} ${value.toLocaleString()}`;
    }
  };

  const statusTemplate = (rowData) => {
    const getSeverity = (status) => {
      switch (status?.toLowerCase()) {
        case "paid":
          return { color: "#4caf50", bgColor: "#e8f5e9" };
        case "partially paid":
          return { color: "#ff9800", bgColor: "#fff3e0" };
        case "pending":
          return { color: "#ff9800", bgColor: "#fff3e0" };
        case "unpaid":
          return { color: "#f57c00", bgColor: "#fff8e1" };
        case "overdue":
          return { color: "#f44336", bgColor: "#ffebee" };
        case "void":
          return { color: "#f44336", bgColor: "#ffebee" };
        case "cancelled":
          return { color: "#9e9e9e", bgColor: "#f5f5f5" };
        default:
          return { color: "#2196f3", bgColor: "#e3f2fd" };
      }
    };

    const statusStyle = getSeverity(rowData.status);

    return (
      <span
        style={{
          fontWeight: "bold",
          color: statusStyle.color,
          padding: "5px 10px",
          borderRadius: "15px",
          backgroundColor: statusStyle.bgColor,
        }}
      >
        {rowData.status}
      </span>
    );
  };

  const actionTemplate = (rowData) => {
    return (
      <div className="flex gap-2">
        <Button
          icon="ti ti-file-text"
          className="p-button-rounded p-button-info p-ml-2"
          onClick={(e) => {
            e.stopPropagation();
            handleViewContract(rowData.whatFor?.description); // Contract ID is stored in whatFor.description
          }}
          tooltip="View Property Management Contract"
        />
      </div>
    );
  };

  const invoiceNumberTemplate = (rowData) => {
    return (
      <Link
        // to={`/facility/levy_management/invoice/${rowData._id}`}
        to={`/facility/property_management/property_invoices/pm_invoice/${rowData._id}`}
        className="text-primary fw-bold"
        style={{ textDecoration: 'none' }}
      >
        {rowData.invoiceNumber}
      </Link>
    );
  };

  const unitTemplate = (rowData) => {
    return (
      <Link
        to={`/facility/unit_management/view_unit/${rowData.unit?.id}`}
        style={{ color: 'royalblue', textDecoration: 'none' }}
      >
        {rowData.unit?.name}
      </Link>
    );
  };

  const amountTemplate = (rowData) => {
    const currencyCode = rowData.currency?.code || 'KES';
    return `${currencyCode} ${rowData.totalAmount.toLocaleString()}`;
  };

  // Client template - displays home owner as client with navigation
  const clientTemplate = (rowData) => {
    // Use clientInfo from backend (which prioritizes home owner)
    const clientInfo = rowData.clientInfo || rowData.client || {};

    let clientName = 'No Client Assigned';
    let clientId = null;

    if (clientInfo.fullName && clientInfo.fullName !== 'No Client Assigned') {
      clientName = clientInfo.fullName;
      clientId = clientInfo.clientId;
    } else if (clientInfo.firstName || clientInfo.lastName) {
      clientName = `${clientInfo.firstName || ''} ${clientInfo.lastName || ''}`.trim();
      clientId = clientInfo.clientId;
    }

    // If we have a valid client, make it clickable
    if (clientId && clientName !== 'No Client Assigned') {
      return (
        <Link
          to={`/facility/customer_management/view_customer/${clientId}`}
          style={{ color: 'royalblue', textDecoration: 'none' }}
        >
          {clientName}
        </Link>
      );
    }

    return clientName;
  };

  const balanceTemplate = (rowData) => {
    const balance = rowData.calculatedBalance || 0;
    return (
      <span
        style={{
          fontWeight: "bold",
          color: balance > 0 ? '#f44336' : '#4caf50'
        }}
      >
        {formatCurrency(balance, rowData.currency?.code || 'KES')}
      </span>
    );
  };

  const filteredInvoices = invoices.filter((invoice) => {
    // Get searchable text from invoice
    const searchableText = [
      invoice.invoiceNumber,
      invoice.clientInfo?.firstName,
      invoice.clientInfo?.lastName,
      invoice.clientInfo?.fullName,
      invoice.client?.firstName,
      invoice.client?.lastName,
      invoice.status,
      invoice.unit?.name
    ]
      .filter(Boolean)
      .join(' ')
      .toLowerCase();

    // Check if any searchable text matches the search keyword
    const matchesSearch = searchKeyword ?
      searchableText.includes(searchKeyword.toLowerCase()) :
      true;

    return matchesSearch;
  });

  const clearFilters = () => {
    setDateFilters({ startDate: null, endDate: null });
    setSearchKeyword("");
    setStatusFilter("");
    setCurrentPage(1);
    fetchInvoices(1);
  };

  const onPageChange = (event) => {
    const newPage = event.page + 1;
    setCurrentPage(newPage);
    fetchInvoices(newPage);
  };

  useEffect(() => {
    if (selectedFacilityId) {
      fetchInvoices(1);
    }
  }, [selectedFacilityId]);

  useEffect(() => {
    if (selectedFacilityId) {
      setCurrentPage(1);
      fetchInvoices(1);
    }
  }, [statusFilter, dateFilters.startDate, dateFilters.endDate]);

  const header = (
    <Toolbar
      className="mb-4"
      left={
        <div className="flex align-items-center gap-3">
          <span className="p-input-icon-left">
            <i className="pi pi-search" />
            <InputText
              placeholder="Search invoices..."
              value={searchKeyword}
              onChange={(e) => setSearchKeyword(e.target.value)}
              style={{ height: "40px" }}
            />
          </span>
          <Dropdown
            value={statusFilter}
            options={statusOptions}
            onChange={(e) => setStatusFilter(e.value)}
            placeholder="All Status"
            style={{ height: "40px" }}
          />
          <Calendar
            value={dateFilters.startDate}
            onChange={(e) =>
              setDateFilters({ ...dateFilters, startDate: e.value })
            }
            placeholder="Start Date"
            style={{ height: "40px" }}
            inputStyle={{ height: "40px" }}
          />
          <Calendar
            value={dateFilters.endDate}
            onChange={(e) =>
              setDateFilters({ ...dateFilters, endDate: e.value })
            }
            placeholder="End Date"
            style={{ height: "40px" }}
            inputStyle={{ height: "40px" }}
          />
        </div>
      }
      right={
        <div className="flex align-items-center gap-2">
          <Button
            label="Filter"
            icon="pi pi-filter"
            onClick={() => fetchInvoices(1)}
            className="p-button-outlined"
            style={{ height: "40px" }}
          />
          <Button
            label="Clear"
            icon="pi pi-times"
            onClick={clearFilters}
            className="p-button-outlined p-button-secondary"
            style={{ height: "40px" }}
          />
        </div>
      }
    />
  );

  return (
    <Layout>
      <div className="page-header">
        <div className="page-block">
          <div className="row align-items-center">
            <div className="col-md-12">
              <ul className="breadcrumb">
                <li className="breadcrumb-item">
                  <Link to="/facility/">Dashboard</Link>
                </li>
                <li className="breadcrumb-item">Property Management</li>
                <li className="breadcrumb-item">Property Management Invoices</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
      <div className="card">
        <div className="card-header">
          <h5>Property Management Invoices</h5>
        </div>
        <div className="card-body">
          <div className="row">
            <div className="col-sm-12">
              <div className="tab-content">
                <Toast ref={toast} />
                <ConfirmDialog />

                <Card>
                  <DataTable
                    value={filteredInvoices}
                    header={header}
                    loading={loading}
                    paginator
                    rows={10}
                    totalRecords={totalInvoices}
                    first={(currentPage - 1) * itemsPerPage}
                    onPage={onPageChange}
                    lazy
                    emptyMessage="No property management invoices found"
                    className="p-datatable-sm"
                    stripedRows
                    selectionMode="single"
                    sortField="issueDate"
                    sortOrder={-1}
                    responsiveLayout="scroll"
                    paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport"
                    currentPageReportTemplate="Showing {first} to {last} of {totalRecords} invoices"
                  >
                    <Column
                      field="invoiceNumber"
                      header="Invoice #"
                      body={invoiceNumberTemplate}
                      style={{ minWidth: "150px" }}
                    />
                    <Column
                      field="unit.name"
                      header="Unit"
                      body={unitTemplate}
                      style={{ minWidth: "120px" }}
                    />
                    <Column
                      field="clientInfo.fullName"
                      header="Client"
                      body={clientTemplate}
                      sortable
                      style={{ minWidth: "180px", maxWidth: "180px" }}
                    />
                    <Column
                      field="totalAmount"
                      header="Amount"
                      body={amountTemplate}
                      style={{ minWidth: "120px" }}
                    />
                    <Column
                      header="Balance"
                      body={balanceTemplate}
                      style={{ minWidth: "120px" }}
                    />
                    <Column
                      field="issueDate"
                      header="Issue Date"
                      body={(rowData) => formatDate(rowData.issueDate)}
                      style={{ minWidth: "120px" }}
                    />
                    <Column
                      field="dueDate"
                      header="Due Date"
                      body={(rowData) => formatDate(rowData.dueDate)}
                      style={{ minWidth: "120px" }}
                    />
                    <Column
                      field="status"
                      header="Status"
                      body={statusTemplate}
                      style={{ minWidth: "120px" }}
                    />
                    <Column
                      field="digitax"
                      header="DigiTax"
                      body={digitaxTemplate}
                      style={{ minWidth: "140px" }}
                    />
                    {/* <Column
                      header="Actions"
                      body={actionTemplate}
                      style={{ minWidth: "100px" }}
                    /> */}
                  </DataTable>
                </Card>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Property Manager Contract Details Dialog */}
      <PropertyManagerContractDetailsDialog
        visible={showContractDetailsDialog}
        onHide={() => setShowContractDetailsDialog(false)}
        contractId={selectedContractId}
      />
    </Layout>
  );
};

export default PropertyManagedInvoices;